import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getUserData, getCollections, getCollectionsComplete } from '../../../assets/js/functions';
import { useLogout } from '../../../hooks/useLogout';

// Íconos
import { MdDashboard, MdAssessment } from 'react-icons/md';
import { FaUserFriends, FaChalkboardTeacher, FaNewspaper, FaTrophy } from 'react-icons/fa';
import { AiOutlineCaretDown, AiOutlineCaretUp } from 'react-icons/ai';
import { BiHomeSmile } from 'react-icons/bi';
import { BiBookAlt } from 'react-icons/bi';
import { IoExit } from 'react-icons/io5';
import { BsPersonGear } from 'react-icons/bs';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';

export const SidebarGlobal = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const [programas, setProgramas] = useState({});
	const [cargado, setCargado] = useState(false);
	const [programasDisponibles, setProgramasDisponibles] = useState([]);
	const logout = useLogout();

	// Estados de apertura de menús
	const [openAdministrar, setOpenAdministrar] = useState(false);
	const [openPrograms, setOpenPrograms] = useState(false);
	const [openUsuarios, setOpenUsuarios] = useState(false);
	const [openCursos, setOpenCursos] = useState(false);
	const [openDashboard, setOpenDashboard] = useState(false);

	// Usuario
	const [perfilUsuario, setPerfilUsuario] = useState('');
	const [usuario, setUsuario] = useState(null);
	const [loadingUser, setLoadingUser] = useState(true);

	// Detectar sección actual
	const pathParts = location.pathname.split('/').filter(Boolean);
	const seccion = pathParts[0] === 'admin' ? pathParts[1] : pathParts[0];
	const seccionDropdown = pathParts[2];

	// ✅ Cargar datos del usuario de forma asíncrona
	useEffect(() => {
		const loadUserData = async () => {
			try {
				setLoadingUser(true);
				const userData = await getUserData();

				if (userData) {
					setPerfilUsuario(userData.perfil || '');
					setUsuario(userData);
				} else {
					console.warn('⚠️ No se pudieron obtener datos del usuario');
				}
				return userData;
			} catch (error) {
				console.error('❌ Error cargando datos del usuario:', error);
			} finally {
				setLoadingUser(false);
			}
		};

		const getData = async () => {
			const { region, programas, cargo } = await loadUserData();
			await obtenerProgramas(region, programas, cargo);
		};
		getData();
	}, [cargado]);

	useEffect(() => {
		const getData = async () => {
			await getProgramas();
			setCargado(true);
		};
		getData();
	}, []);

	const getProgramas = async () => {
		const data = await getCollectionsComplete(`${process.env.REACT_APP_BD_PROGRAMAS}`);
		setProgramas(data);
	};

	const obtenerProgramas = async (usuarioRegion, usuarioProgramas, usuarioCargo) => {
		try {
			const data = await getCollections(`${process.env.REACT_APP_BD_CURSOS}`);
			const cursosActivos = data.filter((curso) => curso.activo === true && curso.regiones.includes(usuarioRegion) && usuarioProgramas.includes(curso.programa) && curso.cargos.includes(usuarioCargo));

			const programasDisponibles = [...new Set(cursosActivos.filter((curso) => usuarioProgramas.includes(curso.programa)).map((x) => programas[x.programa]?.nombre))].sort();
			setProgramasDisponibles(programasDisponibles);
		} catch (error) {
			console.error('Error al obtener programas:', error);
		}
	};

	// Auto-abrir secciones según la ruta
	useEffect(() => {
		// Auto-abrir sección de administrar si está en rutas admin
		if (['usuarios', 'dashboard', 'reportes', 'actividades', 'admin'].includes(seccion)) {
			setOpenAdministrar(true);
		}

		// Auto-abrir submenús
		if (seccion === 'usuarios') setOpenUsuarios(true);
		if (seccion === 'cursos' || seccion === 'actividades') setOpenCursos(true);
	}, [seccion, location.pathname]);

	const goUrl = (url) => navigate(url);

	const toggleAdministrar = () => {
		setOpenAdministrar(!openAdministrar);
	};

	// Helper para verificar si una ruta está activa
	const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

	// ✅ Mostrar loading mientras carga el usuario
	if (loadingUser) {
		return (
			<div className='sidebar-body'>
				<div className='sidebar-menu'>
					<ul className='menu-list'>
						<li className='menu-item'>
							<div className='menu-link'>
								<span className='menu-link-text'>Cargando...</span>
							</div>
						</li>
					</ul>
				</div>
			</div>
		);
	}

	return (
		<div className='sidebar-body'>
			{/* ========================================
			    APRENDIZAJE - Para todos los usuarios
			    ======================================== */}
			<div className='sidebar-menu'>
				<ul className='menu-list'>
					{/* Home */}
					<li className='menu-item'>
						<div onClick={() => goUrl('/home')} className={`menu-link ${isActive('/home') ? 'active' : ''}`}>
							<span className='menu-link-icon'>
								<BiHomeSmile size={16} />
							</span>
							<span className='menu-link-text'>Home</span>
						</div>
					</li>

					{/* Mis Cursos */}
					<li className='menu-item'>
						<div
							onClick={() => {
								setOpenPrograms(!openPrograms);
							}}
							className={`menu-link ${isActive('/cursos') ? 'active' : ''}`}
						>
							<span className='menu-link-icon'>
								<BiBookAlt size={16} />
							</span>
							<span className='menu-link-text'>
								Programas
								<span className='icon-menu'>{openPrograms ? <AiOutlineCaretUp size={12} /> : <AiOutlineCaretDown size={12} />}</span>
							</span>
						</div>
					</li>
					{openPrograms && (
						<ul className='submenu-list'>
							<li className='menu-item'>
								<div onClick={() => (window.location.href = `/cursos`)} className={`menu-link submenu-link`}>
									<span className='menu-link-text'>Todos</span>
								</div>
							</li>
							{programasDisponibles.map((item, index) => (
								<li className='menu-item' key={index}>
									<div onClick={() => (window.location.href = `/cursos?programa=${item}`)} className={`menu-link submenu-link`}>
										<span className='menu-link-text'>{item}</span>
									</div>
								</li>
							))}
						</ul>
					)}

					{/* Noticias - ACTIVADA */}
					<li className='menu-item'>
						<div onClick={() => goUrl('/noticias')} className={`menu-link ${isActive('/noticias') || isActive('/noticia') ? 'active' : ''}`}>
							<span className='menu-link-icon'>
								<FaNewspaper size={16} />
							</span>
							<span className='menu-link-text'>Noticias</span>
						</div>
					</li>

					{/* Perfil */}
					<li className='menu-item'>
						<div onClick={() => goUrl('/perfil')} className={`menu-link ${isActive('/perfil') ? 'active' : ''}`}>
							<span className='menu-link-icon'>
								<ManageAccountsIcon size={16} />
							</span>
							<span className='menu-link-text'>Perfil</span>
						</div>
					</li>
				</ul>
			</div>

			{/* ========================================
			    ADMINISTRAR - Solo Super Administrador
			    ======================================== */}
			{perfilUsuario === 'Super Administrador' && (
				<div className='sidebar-menu sidebar-menu-admin'>
					<div className='menu-section-header'>
						<div onClick={toggleAdministrar} className='menu-section-toggle'>
							<span className='section-icon'>
								<BsPersonGear size={16} />
							</span>
							<span className='section-title'>Administrar</span>
							<span className='toggle-icon'>{openAdministrar ? <AiOutlineCaretUp size={14} /> : <AiOutlineCaretDown size={14} />}</span>
						</div>
					</div>
					{openAdministrar && (
						<ul className='menu-list'>
							{/* Dashboard */}

							<li className='menu-item'>
								<div
									onClick={() => {
										setOpenDashboard(!openDashboard);
										if (!openDashboard) {
											setOpenCursos(false);
										}
									}}
									className={`menu-link ${isActive('/admin/dashboard') ? 'active' : ''}`}
								>
									<span className='menu-link-icon'>
										<MdDashboard size={16} />
									</span>
									<span className='menu-link-text'>
										Dashboard
										<span className='icon-menu'>{openDashboard ? <AiOutlineCaretUp size={12} /> : <AiOutlineCaretDown size={12} />}</span>
									</span>
								</div>
							</li>
							{openDashboard && (
								<ul className='submenu-list'>
									<li className='menu-item'>
										<div onClick={() => goUrl('/admin/dashboard')} className={`menu-link submenu-link ${location.pathname === '/admin/dashboard' ? 'active' : ''}`}>
											<span className='menu-link-text'>Plataforma</span>
										</div>
									</li>
									<li className='menu-item'>
										<div onClick={() => goUrl('/admin/asistente-ia')} className={`menu-link submenu-link ${location.pathname === '/admin/asistente-ia' ? 'active' : ''}`}>
											<span className='menu-link-text'>Asistente IA</span>
										</div>
									</li>
								</ul>
							)}

							{/* ===== USUARIOS ===== */}
							<li className='menu-item'>
								<div
									onClick={() => {
										setOpenUsuarios(!openUsuarios);
										if (!openUsuarios) {
											setOpenCursos(false);
										}
									}}
									className={`menu-link ${isActive('/admin/usuarios') ? 'active' : ''}`}
								>
									<span className='menu-link-icon'>
										<FaUserFriends size={16} />
									</span>
									<span className='menu-link-text'>
										Usuarios
										<span className='icon-menu'>{openUsuarios ? <AiOutlineCaretUp size={12} /> : <AiOutlineCaretDown size={12} />}</span>
									</span>
								</div>
							</li>
							{openUsuarios && (
								<ul className='submenu-list'>
									<li className='menu-item'>
										<div onClick={() => goUrl('/admin/usuarios')} className={`menu-link submenu-link ${location.pathname === '/admin/usuarios' || seccionDropdown === 'actualizar' || seccionDropdown === 'crear' ? 'active' : ''}`}>
											<span className='menu-link-text'>Lista</span>
										</div>
									</li>
									<li className='menu-item'>
										<div onClick={() => goUrl('/admin/usuarios/carga-masiva')} className={`menu-link submenu-link ${seccionDropdown === 'carga-masiva' ? 'active' : ''}`}>
											<span className='menu-link-text'>Carga masiva</span>
										</div>
									</li>
								</ul>
							)}

							{/* ===== CURSOS ===== */}
							<li className='menu-item'>
								<div
									onClick={() => {
										setOpenCursos(!openCursos);
										if (!openCursos) {
											setOpenUsuarios(false);
										}
									}}
									className={`menu-link ${isActive('/admin/cursos') || isActive('/admin/actividades') ? 'active' : ''}`}
								>
									<span className='menu-link-icon'>
										<FaChalkboardTeacher size={16} />
									</span>
									<span className='menu-link-text'>
										Cursos
										<span className='icon-menu'>{openCursos ? <AiOutlineCaretUp size={12} /> : <AiOutlineCaretDown size={12} />}</span>
									</span>
								</div>
							</li>
							{openCursos && (
								<ul className='submenu-list'>
									<li className='menu-item'>
										<div onClick={() => goUrl('/admin/cursos')} className={`menu-link submenu-link ${location.pathname === '/admin/cursos' || location.pathname.includes('/admin/cursos/') ? 'active' : ''}`}>
											<span className='menu-link-text'>Lista de cursos</span>
										</div>
									</li>
								</ul>
							)}

							{/* Noticias - ACTIVADA */}
							<li className='menu-item'>
								<div onClick={() => goUrl('/admin/noticias')} className={`menu-link ${isActive('/admin/noticias') ? 'active' : ''}`}>
									<span className='menu-link-icon'>
										<FaNewspaper size={16} />
									</span>
									<span className='menu-link-text'>Noticias</span>
								</div>
							</li>

						</ul>
					)}
				</div>
			)}

			{/* ========================================
			    CERRAR SESIÓN
			    ======================================== */}
			<div className='sidebar-menu sidebar-menu-bottom'>
				<ul className='menu-list'>
					<li className='menu-item'>
						<div onClick={logout} className='menu-link'>
							<span className='menu-link-icon'>
								<IoExit size={16} />
							</span>
							<span className='menu-link-text'>Cerrar sesión</span>
						</div>
					</li>
				</ul>
			</div>
		</div>
	);
};
